import React from 'react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { ServiceList } from '../components/ui/ServiceGroups';
import { CtaBand } from '../components/ui/CtaBand';
import { Co2Report } from '../components/features/Co2Report';
import { Faq } from '../components/ui/Faq';
import { IMAGES, SUSTAINABILITY_ITEMS } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const FAQS = [{
  question: '¿Qué se mide exactamente en la huella de carbono?',
  answer: 'Las emisiones asociadas a la operación gestionada: transporte y servicios ejecutados. El alcance se define con el cliente al inicio y se informa de forma mensual.'
}, {
  question: '¿Qué se hace con la medición?',
  answer: 'Se usa como base para identificar oportunidades concretas: rediseño de tramos y frecuencias, ocupación de unidades, modernización de flota y tecnologías con menor impacto.'
}, {
  question: '¿Rabemar publica indicadores propios de sustentabilidad?',
  answer: 'Sólo se publican indicadores medidos y validados. Los valores de cada operación pertenecen al cliente y se informan en sus reportes.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Soluciones',
  path: '/soluciones'
}, {
  name: 'Sustentabilidad',
  path: '/soluciones/sustentabilidad'
}];
export function Sustainability() {
  useSeo({
    title: 'Sustentabilidad logística | Huella de carbono y reportes de CO₂ — Rabemar',
    description: 'Medición de huella de carbono, reportes mensuales de CO₂, análisis de operaciones, modernización de flota e indicadores ambientales aplicados a la operación.',
    path: '/soluciones/sustentabilidad',
    image: IMAGES.sustainability,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('Sustentabilidad operativa y medición de huella de carbono', 'Medición de huella de carbono, reportes mensuales de CO₂, análisis de operaciones, indicadores ambientales y planes de reducción.', '/soluciones/sustentabilidad'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="06 — Sustentabilidad" title="Medir. Reducir. Mejorar." lead="Sustentabilidad tratada como parte del control operativo: primero se mide, después se decide y por último se verifica el efecto de cada acción." image={IMAGES.sustainability} imageAlt="Vista aérea de un centro de distribución con paneles solares en el techo rodeado de áreas verdes" crumbs={CRUMBS} ctaLabel="Solicitar propuesta" ctaService="Sustentabilidad" data-id="element-828" data-mp="3862nrm7mbkhh" />

      <section className="bg-white py-20 md:py-28" data-id="element-829" data-mp="3prqzztdge1o">
        <Container data-id="element-830" data-mp="3p8a8mfgjvp8">
          <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-831" data-mp="3rug8hmpa35c">
            <div data-id="element-832" data-mp="8qxmg5ef2few">
              <Eyebrow data-id="element-833" data-mp="8n9uhaome12a">Programa ambiental</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-834" data-mp="dp8x2icjcty6">
                Del reporte a la acción operativa.
              </Heading>
              <p className="mt-5 text-lg leading-relaxed text-graphite-500" data-id="element-835" data-mp="nq1h25x3lck3">
                La medición de emisiones sólo tiene valor si cambia decisiones: rutas, ocupación de unidades, tecnología
                y renovación de flota.
              </p>
              <div className="mt-9" data-id="element-836" data-mp="9t4q84gzbv37">
                <ServiceList items={SUSTAINABILITY_ITEMS} data-id="element-837" data-mp="9u640gpdqvom" />
              </div>
              <p className="mt-9 border border-mist-200 bg-mist-50 p-5 text-sm leading-relaxed text-graphite-500" data-id="element-838" data-mp="16hauurdwinhh">
                El programa se alinea conceptualmente con los Objetivos de Desarrollo Sostenible de Naciones Unidas en los
                aspectos donde existe respaldo real en la operación medida. No se declaran certificaciones ni adhesiones
                formales que no estén acreditadas.
              </p>
            </div>
            <Co2Report data-id="element-839" data-mp="1kieuf0xqd1nf" />
          </div>
        </Container>
      </section>

      <section className="bg-brand-900 py-20 md:py-28" data-id="element-840" data-mp="1kh3nwtcwhfsm">
        <Container data-id="element-841" data-mp="15040u38y9yvc">
          <div className="grid gap-px bg-white/10 md:grid-cols-3" data-id="element-842" data-mp="14zluuewhk6a8">
            {[{
            t: 'Medir',
            d: 'Definición de alcance, recolección de datos de la operación y cálculo de emisiones con frecuencia mensual.'
          }, {
            t: 'Reducir',
            d: 'Identificación de oportunidades: consolidación de cargas, ocupación de unidades, modernización de flota y tecnologías sustentables.'
          }, {
            t: 'Mejorar',
            d: 'Seguimiento de indicadores ambientales y verificación del efecto de cada acción sobre la operación.'
          }].map((item, i) => <div key={item.t} className="bg-brand-900 p-8" data-id="element-843" data-mp="1iybvg4iiko3c" data-mpi={i}>
                <span className="font-display text-sm font-bold tabular-nums text-accent-400" data-id="element-844" data-mp="26ph8yd0zf4ah">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-white" data-id="element-845" data-mp="1gwwq45ensr">{item.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist-200" data-id="element-846" data-mp="3r81j9wj9d7jg">{item.d}</p>
              </div>)}
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-847" data-mp="9tcw64hz0jyk">
        <Container data-id="element-848" data-mp="4rxe8lb3atke">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-849" data-mp="4sp8nssyal93">
            <div data-id="element-850" data-mp="3d91vss7heyov">
              <Eyebrow data-id="element-851" data-mp="3d8ip67na7wq5">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-852" data-mp="3895skv6lzbad">
                Sustentabilidad
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-853" data-mp="3n77tsyq63kog" />
          </div>
        </Container>
      </section>

      <CtaBand title="Empecemos por medir su operación." text="Definimos alcance, fuentes de datos y frecuencia de reporte para tener una línea de base sobre la cual decidir." primaryLabel="Solicitar propuesta" location="sustentabilidad-cta" service="Sustentabilidad" data-id="element-854" data-mp="3i6tz0idku78d" />
    </>;
}